import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  RegionsStateType,
  PricingType,
  PolygonFiltersType,
  PricingMiddlewareType,
  OriginStateType,
} from "./types/regions";
import { RootState } from "store";
import {
  OriginType,
  RegionDetailType,
  RegionType,
  UpdateWorkzoneCapacityPayloadType,
  WorkzoneType,
} from "sagas/types/regions";
import { PolygonsTabsType } from "pages/polygons/types";
import { PolygonGeneralForm } from "pages/polygons/general/types";
import { RainType } from "sagas/types/rainModal";
import { FilterTransactionsType } from "sagas/types/transactions";

export const initialState: RegionsStateType = {
  fetchingWorkzones: false,
  fetchingOrigins: false,
  showModal: false,
  showRainModal: false,
  actualPage: "general",
  regions: [],
  origins: [],
  workzones: [],
  filters: {
    region: null,
    updated: false,
  },
  pricing: null,
  actualRegion: null,
  nextRain: null,
};

const SLICE_NAME = "region";

export const regionSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    getRegionsRequest: () => {},
    getRegionsError: () => {},
    getRegionsSuccess: (
      state: RegionsStateType,
      action: PayloadAction<RegionType[]>
    ) => {
      const { payload } = action;
      state.regions = payload;
    },
    getOriginsFetch: (
      state: OriginStateType, 
      action: PayloadAction<FilterTransactionsType>) => {
      state.fetchingOrigins = true;
    },
    getOriginsError: (state: OriginStateType) => {
      state.fetchingOrigins = false;
    },
    getOriginsSuccess: (
      state: OriginStateType,
      action: PayloadAction<OriginType[]>
    ) => {
      const { payload } = action;
      state.origins = payload;
      state.fetchingOrigins = false;
    },
    resetRegions: (state) => {
      state.regions = [];
    },
    resetOrigins: (state) => {
      state.origins = [];
    },
    getWorkzonesFetch: (
      state: RegionsStateType,
      action: PayloadAction<string>
    ) => {
      state.fetchingWorkzones = true;
    },
    getWorkzonesError: (state: RegionsStateType) => {
      state.fetchingWorkzones = false;
    },
    getWorkzonesSuccess: (
      state: RegionsStateType,
      action: PayloadAction<WorkzoneType[]>
    ) => {
      const { payload } = action;
      state.fetchingWorkzones = false;
      state.workzones = payload;
    },
    getPricingDetailRequest: (
      state: RegionsStateType,
      action: PayloadAction<string>
    ) => {},
    getPricingDetailError: (state: RegionsStateType) => {},
    getPricingDetailSuccess: (
      state: RegionsStateType,
      action: PayloadAction<PricingType>
    ) => {
      const { payload } = action;
      state.pricing = payload;
    },
    clearWorkzones: (state: RegionsStateType) => {
      state.workzones = [];
    },
    clearOrigins: (state: OriginStateType) => {
      state.origins = [];
    },
    getRegionDetailRequest: (
      state: RegionsStateType,
      action: PayloadAction<string>
    ) => {},
    getRegionDetailError: (state: RegionsStateType) => {
      state.actualRegion = null;
    },
    getRegionDetailSuccess: (
      state: RegionsStateType,
      action: PayloadAction<RegionDetailType>
    ) => {
      const { payload } = action;
      state.actualRegion = payload;
    },
    updateRegionDetailRequest: (
      state: RegionsStateType,
      action: PayloadAction<PolygonGeneralForm>
    ) => {},
    updateRegionDetailError: () => {},
    updateRegionDetailSuccess: (state: RegionsStateType) => {},

    updatePricingDetailRequest: (
      state: RegionsStateType,
      action: PayloadAction<PricingMiddlewareType>
    ) => {},
    updatePricingDetailError: () => {},
    updatePricingDetailSuccess: () => {},

    updateWorkzoneCapacityRequest: (
      state: RegionsStateType,
      action: PayloadAction<UpdateWorkzoneCapacityPayloadType>
    ) => {},
    updateWorkzoneCapacityError: () => {},
    updateWorkzoneCapacitySuccess: (state: RegionsStateType) => {
      state.showModal = false;
    },
    setActualPage: (
      state: RegionsStateType,
      action: PayloadAction<PolygonsTabsType>
    ) => {
      state.actualPage = action.payload;
    },
    setFilters: (
      state: RegionsStateType,
      action: PayloadAction<PolygonFiltersType>
    ) => {
      state.filters = action.payload;
    },
    toggleModalVisibility: (
      state: RegionsStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showModal = action.payload;
    },
    toggleRainModalVisibility: (
      state: RegionsStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showRainModal = action.payload;
    },
    resetFilter: (state: RegionsStateType) => {
      state.filters = initialState.filters;
      state.actualRegion = null;
    },
    resetNextRain: (state: RegionsStateType) => {
      state.nextRain = initialState.nextRain;
    },
    resetPricing: (state: RegionsStateType) => {
      state.pricing = initialState.pricing;
    },
    setFilterUpdated: (
      state: RegionsStateType,
      action: PayloadAction<boolean>
    ) => {
      state.filters.updated = action.payload;
    },
    getNextRainRequest: (
      state: RegionsStateType,
      action: PayloadAction<string>
    ) => {},
    getNextRainError: () => {},
    getNextRainSuccess: (
      state: RegionsStateType,
      action: PayloadAction<RainType>
    ) => {
      state.nextRain = action.payload;
    },
  },
});

export const regionSelector = (state: RootState) => state.regions;

export const actions = regionSlice.actions;

export default regionSlice.reducer;
